import { QueryClient, dehydrate, hydrate } from '@tanstack/react-query';

type PersistConfig = {
  storageKey?: string;
  maxAgeMs?: number;
  shouldDehydrateQuery?: (q: any) => boolean;
};

const defaultDBName = 'RQCacheDB';
const defaultStoreName = 'rq_cache_store';

// IndexedDB bulunmayan ortamlar (Jest/jsdom, bazı gizli pencereler) için koruma;
// bu ortamlarda persist sessizce devre dışı kalır, uygulama çökmez.
export const isIndexedDBAvailable = () => typeof indexedDB !== 'undefined' && indexedDB != null;

function openDB(dbName = defaultDBName, storeName = defaultStoreName): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(dbName, 1);
    request.onupgradeneeded = (event: any) => {
      const db: IDBDatabase = event.target.result;
      if (!db.objectStoreNames.contains(storeName)) {
        db.createObjectStore(storeName, { keyPath: 'key' });
      }
    };
    request.onsuccess = (event: any) => resolve(event.target.result as IDBDatabase);
    request.onerror = (event: any) => reject(event.target.error);
  });
}

export async function idbSet(key: string, value: any, dbName?: string, storeName?: string): Promise<void> {
  const db = await openDB(dbName, storeName);
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(storeName || defaultStoreName, 'readwrite');
    const store = tx.objectStore(storeName || defaultStoreName);
    const req = store.put({ key, value });
    req.onsuccess = () => {};
    req.onerror = () => reject(req.error);
    tx.oncomplete = () => {
      db.close();
      resolve();
    };
    tx.onerror = () => {
      const err = (tx as any).error;
      db.close();
      reject(err);
    };
    tx.onabort = () => {
      const err = (tx as any).error || new Error('Transaction aborted');
      db.close();
      reject(err);
    };
  });
}

export async function idbGet<T = any>(key: string, dbName?: string, storeName?: string): Promise<T | null> {
  const db = await openDB(dbName, storeName);
  return new Promise<T | null>((resolve, reject) => {
    const tx = db.transaction(storeName || defaultStoreName, 'readonly');
    const store = tx.objectStore(storeName || defaultStoreName);
    const req = store.get(key);
    req.onsuccess = () => resolve((req.result && req.result.value) || null);
    req.onerror = () => reject(req.error);
    tx.oncomplete = () => db.close();
    tx.onerror = () => {
      const err = (tx as any).error;
      db.close();
      reject(err);
    };
    tx.onabort = () => {
      const err = (tx as any).error || new Error('Transaction aborted');
      db.close();
      reject(err);
    };
  });
}

export async function idbDelete(key: string, dbName?: string, storeName?: string): Promise<void> {
  const db = await openDB(dbName, storeName);
  await new Promise<void>((resolve, reject) => {
    const tx = db.transaction(storeName || defaultStoreName, 'readwrite');
    const store = tx.objectStore(storeName || defaultStoreName);
    const req = store.delete(key);
    req.onsuccess = () => {};
    req.onerror = () => reject(req.error);
    tx.oncomplete = () => {
      db.close();
      resolve();
    };
    tx.onerror = () => {
      const err = (tx as any).error;
      db.close();
      reject(err);
    };
    tx.onabort = () => {
      const err = (tx as any).error || new Error('Transaction aborted');
      db.close();
      reject(err);
    };
  });
}

export function initQueryPersistence(queryClient: QueryClient, cfg: PersistConfig = {}) {
  if (!isIndexedDBAvailable()) {
    return () => {};
  }
  const storageKey = cfg.storageKey || 'rq-cache';
  const maxAgeMs = cfg.maxAgeMs ?? 7 * 24 * 60 * 60 * 1000;
  const shouldDehydrateQuery = cfg.shouldDehydrateQuery || ((q: any) => q?.meta?.persist === true && q?.state?.status === 'success');

  let isHydrated = false;
  let unsub: (() => void) | null = null;
  let pending = false;
  let lastSave = 0;

  const save = async () => {
    const now = Date.now();
    if (pending) return;
    if (now - lastSave < 500) return;
    pending = true;
    try {
      const dehydrated = dehydrate(queryClient, { shouldDehydrateQuery });
      // Serialize to ensure no non-serializable values (Promises, functions) reach IDB
      const payload = { ts: Date.now(), state: dehydrated };
      await idbSet(storageKey, JSON.stringify(payload));
      lastSave = Date.now();
    } finally {
      pending = false;
    }
  };

  (async () => {
    try {
      const raw = await idbGet<any>(storageKey);
      const payload = typeof raw === 'string' ? JSON.parse(raw) : raw;
      if (payload && payload.state && (Date.now() - payload.ts) <= maxAgeMs) {
        try {
          hydrate(queryClient, payload.state);
        } catch (_e) {
          // Malformed or incompatible cache; clear it to avoid runtime errors
          await idbDelete(storageKey);
        }
      } else {
        if (payload) await idbDelete(storageKey);
      }
    } catch (_e) {
      // Cache okunamadıysa (idb hatası vb.) persist'siz devam et; uygulamayı düşürme
    } finally {
      isHydrated = true;
      unsub = queryClient.getQueryCache().subscribe(() => {
        if (!isHydrated) return;
        save();
      }) as unknown as (() => void);
      window.addEventListener('beforeunload', save);
    }
  })();

  return () => {
    if (unsub) unsub();
    window.removeEventListener('beforeunload', save);
  };
}
